import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, gql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { toastr } from 'react-redux-toastr';
import { FormattedMessage } from 'react-intl';
import messages from '../../../locale/messages';

import { 
  MapPin, 
  Type, 
  Settings, 
  Plus, 
  Trash2, 
  Save, 
  CheckCircle,
  Globe,
  Navigation
} from 'lucide-react';

import s from './MapSectionManagement.css';
import cs from '../../commonStyle.css';
import MultiLanguageInput from '../MultiLanguageInput/MultiLanguageInput';
import ml from '../MultiLanguageInput/MultiLanguageInput.css';

class MapSectionManagement extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      // Multilingual fields
      title: 'Find your freedom. Wherever you are.',
      title_en: '',
      title_fr: '',
      title_it: '',
      title_pt: '',
      subtitle: 'Discover extraordinary boats in the world\'s most beautiful destinations',
      subtitle_en: '',
      subtitle_fr: '',
      subtitle_it: '',
      subtitle_pt: '',
      // Settings
      autoGeolocation: true,
      defaultZoom: 10,
      destinations: [
        { id: 1, name: 'Miami', lat: 25.7617, lng: -80.1918, zoom: 11 },
        { id: 2, name: 'Cancún', lat: 21.1619, lng: -86.8515, zoom: 12 },
        { id: 3, name: 'Cartagena', lat: 10.3910, lng: -75.4794, zoom: 12 },
        { id: 4, name: 'Ibiza', lat: 38.9067, lng: 1.4206, zoom: 12 },
        { id: 5, name: 'Punta Cana', lat: 18.5601, lng: -68.3725, zoom: 12 },
      ],
      loading: false,
      success: false
    };
  }

  componentDidMount() {
    const { data } = this.props;
    if (data && data.getMapSectionSettings) {
      this.loadSettingsFromData(data.getMapSectionSettings);
    }
  }

  componentDidUpdate(prevProps) {
    const { data } = this.props;
    if (data && data.getMapSectionSettings && prevProps.data !== data) {
      this.loadSettingsFromData(data.getMapSectionSettings);
    }
  }

  loadSettingsFromData = (settings) => {
    if (!settings) return;
    this.setState({
      title: settings.title || this.state.title,
      title_en: settings.title_en || '',
      title_fr: settings.title_fr || '',
      title_it: settings.title_it || '',
      title_pt: settings.title_pt || '',
      subtitle: settings.subtitle || this.state.subtitle,
      subtitle_en: settings.subtitle_en || '',
      subtitle_fr: settings.subtitle_fr || '',
      subtitle_it: settings.subtitle_it || '',
      subtitle_pt: settings.subtitle_pt || '',
      autoGeolocation: settings.autoGeolocation !== false,
      defaultZoom: settings.defaultZoom || 10,
      destinations: settings.destinations ? JSON.parse(settings.destinations) : this.state.destinations
    });
  };

  handleInputChange = (field, value) => {
    this.setState({ [field]: value, success: false });
  };

  handleDestinationChange = (index, field, value) => {
    const { destinations } = this.state;
    const updated = [...destinations];
    updated[index] = { ...updated[index], [field]: field === 'name' ? value : parseFloat(value) || 0 };
    this.setState({ destinations: updated, success: false });
  };

  addDestination = () => {
    const { destinations } = this.state;
    const newId = Math.max(...destinations.map(d => d.id), 0) + 1;
    this.setState({
      destinations: [...destinations, { id: newId, name: '', lat: 0, lng: 0, zoom: 12 }],
      success: false
    });
  };

  removeDestination = (index) => {
    const { destinations } = this.state;
    if (destinations.length <= 1) {
      toastr.warning('Warning', 'You need at least one destination');
      return;
    }
    const updated = destinations.filter((_, i) => i !== index);
    this.setState({ destinations: updated, success: false });
  };

  handleSave = async () => {
    const { updateMapSectionSettings } = this.props;
    const { 
      title, title_en, title_fr, title_it, title_pt,
      subtitle, subtitle_en, subtitle_fr, subtitle_it, subtitle_pt,
      autoGeolocation, defaultZoom, destinations 
    } = this.state;

    // Validate
    if (!title.trim()) {
      toastr.error('Error', 'Title is required');
      return;
    }

    const validDestinations = destinations.filter(d => d.name.trim() && d.lat && d.lng);
    if (validDestinations.length === 0) {
      toastr.error('Error', 'At least one valid destination is required');
      return;
    }

    this.setState({ loading: true });

    try {
      const { data } = await updateMapSectionSettings({
        variables: {
          title,
          title_en,
          title_fr,
          title_it,
          title_pt,
          subtitle,
          subtitle_en,
          subtitle_fr,
          subtitle_it,
          subtitle_pt,
          autoGeolocation,
          defaultZoom: parseInt(defaultZoom),
          destinations: JSON.stringify(validDestinations)
        }
      });

      if (data && data.updateMapSectionSettings && data.updateMapSectionSettings.status === 'success') {
        this.setState({ success: true });
        toastr.success('Success', 'Map section settings saved successfully');
      } else {
        toastr.error('Error', 'Failed to save settings');
      }
    } catch (error) {
      console.error('Save error:', error);
      toastr.error('Error', 'Failed to save settings');
    }

    this.setState({ loading: false });
  };

  render() {
    const { title: pageTitle } = this.props;
    const { autoGeolocation, defaultZoom, destinations, loading, success } = this.state;

    return (
      <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
        <h1 className={cs.adminContentTitle}><FormattedMessage {...messages.mapSectionTitle} /></h1>

        {success && (
          <div className={s.successMsg}>
            <CheckCircle size={18} />
            Settings saved successfully!
          </div>
        )}

        {/* Texts Section */}
        <div className={s.sectionCard}>
          <h3 className={s.sectionTitle}>
            <Type size={20} />
            Section Texts
          </h3>
          
          <MultiLanguageInput
            baseField="title"
            values={this.state}
            onChange={this.handleInputChange}
            inputType="text"
            label="Title"
            placeholder="Find your freedom. Wherever you are."
          />

          <MultiLanguageInput
            baseField="subtitle"
            values={this.state}
            onChange={this.handleInputChange}
            inputType="textarea"
            label="Subtitle"
            placeholder="Discover extraordinary boats..."
          />

          {/* Preview */}
          <div className={s.previewBox}>
            <div className={s.previewTitle}>{this.state.title}</div>
            <div className={s.previewSubtitle}>{this.state.subtitle}</div>
          </div>
        </div>

        {/* Settings Section */}
        <div className={s.sectionCard}>
          <h3 className={s.sectionTitle}>
            <Settings size={20} />
            Behavior Settings
          </h3>

          <div className={s.switchRow}>
            <div>
              <div className={s.switchLabel}>Auto Geolocation</div>
              <div className={s.switchDescription}>
                If enabled, the map will try to center on the user's location. If disabled or denied, it will show all boats.
              </div>
            </div>
            <label className={s.toggleSwitch}>
              <input
                type="checkbox"
                checked={autoGeolocation}
                onChange={(e) => this.handleInputChange('autoGeolocation', e.target.checked)}
              />
              <span className={s.toggleSlider}></span>
            </label>
          </div>

          <div className={s.formGroup} style={{ marginTop: '20px' }}>
            <label>Default Zoom Level (3-18)</label>
            <input
              type="number"
              min="3"
              max="18"
              value={defaultZoom}
              onChange={(e) => this.handleInputChange('defaultZoom', e.target.value)}
              style={{ width: '100px' }}
            />
          </div>
        </div>

        {/* Destinations Section */}
        <div className={s.sectionCard}>
          <h3 className={s.sectionTitle}>
            <MapPin size={20} />
            Featured Destinations
          </h3>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
            These destinations appear as quick navigation buttons on the map.
          </p>

          <div className={s.destinationList}>
            {destinations.map((dest, index) => (
              <div key={dest.id} className={s.destinationItem}>
                <Navigation size={18} style={{ color: '#3b82f6', flexShrink: 0 }} />
                <input
                  type="text"
                  value={dest.name}
                  onChange={(e) => this.handleDestinationChange(index, 'name', e.target.value)}
                  placeholder="City name"
                  style={{ flex: 2 }}
                />
                <div className={s.coordInputs}>
                  <input
                    type="number"
                    step="0.0001"
                    value={dest.lat}
                    onChange={(e) => this.handleDestinationChange(index, 'lat', e.target.value)}
                    placeholder="Latitude"
                  />
                  <input
                    type="number"
                    step="0.0001"
                    value={dest.lng}
                    onChange={(e) => this.handleDestinationChange(index, 'lng', e.target.value)}
                    placeholder="Longitude"
                  />
                  <input
                    type="number"
                    min="3"
                    max="18"
                    value={dest.zoom}
                    onChange={(e) => this.handleDestinationChange(index, 'zoom', e.target.value)}
                    placeholder="Zoom"
                    style={{ width: '70px' }}
                  />
                </div>
                <button
                  type="button"
                  className={s.removeBtn}
                  onClick={() => this.removeDestination(index)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>

          <button type="button" className={s.addBtn} onClick={this.addDestination}>
            <Plus size={18} />
            Add Destination
          </button>
        </div>

        {/* Save Button */}
        <div className={s.actionBar}>
          <button
            type="button"
            className={s.saveBtn}
            onClick={this.handleSave}
            disabled={loading}
          >
            <Save size={18} />
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    );
  }
}

const mapState = (state) => ({});

export default compose(
  withStyles(s, cs, ml),
  connect(mapState),
  graphql(gql`
    query getMapSectionSettings {
      getMapSectionSettings {
        id
        title
        title_en
        title_fr
        title_it
        title_pt
        subtitle
        subtitle_en
        subtitle_fr
        subtitle_it
        subtitle_pt
        autoGeolocation
        defaultZoom
        destinations
      }
    }
  `, {
    options: {
      fetchPolicy: 'network-only'
    }
  }),
  graphql(gql`
    mutation updateMapSectionSettings(
      $title: String!
      $title_en: String
      $title_fr: String
      $title_it: String
      $title_pt: String
      $subtitle: String
      $subtitle_en: String
      $subtitle_fr: String
      $subtitle_it: String
      $subtitle_pt: String
      $autoGeolocation: Boolean
      $defaultZoom: Int
      $destinations: String
    ) {
      updateMapSectionSettings(
        title: $title
        title_en: $title_en
        title_fr: $title_fr
        title_it: $title_it
        title_pt: $title_pt
        subtitle: $subtitle
        subtitle_en: $subtitle_en
        subtitle_fr: $subtitle_fr
        subtitle_it: $subtitle_it
        subtitle_pt: $subtitle_pt
        autoGeolocation: $autoGeolocation
        defaultZoom: $defaultZoom
        destinations: $destinations
      ) {
        status
      }
    }
  `, {
    name: 'updateMapSectionSettings'
  })
)(MapSectionManagement);
